/* Locally-curated rotation of 7 devotionals (one per weekday).
 *
 * Why local?
 * The upstream landing API (cemedeiros.com.br/api/devotional/today) currently
 * returns a single devotional and has no date-based calendar. To deliver a
 * reliable "different devotional each day" experience, we bundle a small
 * rotation of pastoral reflections here. When the landing API evolves to expose
 * multiple devotionals, the rotation helper will prefer the upstream payload.
 *
 * Texts are original pastoral reflections by C E Medeiros, stylistically
 * aligned with the existing devotional in production.
 */

export const DEVOTIONAL_ROTATION = [
  {
    id: "rot-sunday",
    title: "Descansando em Deus",
    verse_ref: "Salmos 23:1-2",
    verse_text:
      "O Senhor é o meu pastor, nada me faltará. Deitar-me faz em verdes pastos, guia-me mansamente a águas tranquilas.",
    reflection:
      "O domingo nos convida a parar. Em meio a uma semana de tarefas, Deus nos lembra que o descanso é bênção, não preguiça. Assim como o pastor conduz suas ovelhas a águas tranquilas, Deus quer levar você a um lugar de paz, mesmo no meio do barulho.",
    application:
      "Hoje, reserve vinte minutos em silêncio. Sem celular, sem lista de afazeres.",
    prayer:
      "Senhor, aquieta a minha alma. Ensina-me a confiar e a descansar em Ti. Amém.",
    estimated_minutes: 3,
  },
  {
    id: "rot-monday",
    title: "Começando com Propósito",
    verse_ref: "Provérbios 16:3",
    verse_text:
      "Confia ao Senhor as tuas obras, e teus pensamentos serão estabelecidos.",
    reflection:
      "Segunda-feira pede direção. Antes das reuniões, dos e-mails, das decisões, o primeiro ato espiritual é entregar a semana ao Senhor. Trabalho sem oração vira peso; trabalho com oração vira missão.",
    application:
      "Antes de abrir a agenda, ore nomeando cada compromisso importante do dia.",
    prayer:
      "Deus, confio a Ti esta semana. Sustenta as minhas mãos e guarda o meu coração. Amém.",
    estimated_minutes: 3,
  },
  {
    id: "rot-tuesday",
    title: "Fé em Meio ao Dia Comum",
    verse_ref: "Hebreus 11:1",
    verse_text:
      "Ora, a fé é o firme fundamento das coisas que se esperam, e a prova das coisas que se não veem.",
    reflection:
      "A fé verdadeira não se prova apenas em grandes crises, ela se mostra no dia comum. Quando ninguém está olhando, quando o trabalho cansa, quando a pressão chega, é ali que o cristão revela em Quem realmente confia.",
    application:
      "Identifique um momento hoje em que você foi tentado a desanimar e escolha agir pela fé.",
    prayer:
      "Senhor, fortalece a minha fé nos pequenos gestos. Que eu Te veja no comum. Amém.",
    estimated_minutes: 4,
  },
  {
    id: "rot-wednesday",
    title: "A Força que Vem da Palavra",
    verse_ref: "Josué 1:8",
    verse_text:
      "Não se aparte da tua boca o livro desta lei; antes medita nele dia e noite, para que tenhas cuidado de fazer conforme a tudo quanto nele está escrito.",
    reflection:
      "No meio da semana, o cansaço bate e o ânimo oscila. Mas há uma fonte de revigoramento que nunca seca: a Palavra de Deus. Ela não é para ser lida de passagem, é para ser meditada, absorvida, obedecida.",
    application:
      "Escolha um versículo curto e carregue-o no bolso ou no celular. Releia três vezes hoje.",
    prayer:
      "Pai, que a Tua Palavra seja luz para o meu caminho hoje. Amém.",
    estimated_minutes: 3,
  },
  {
    id: "rot-thursday",
    title: "Oração que Sustenta",
    verse_ref: "1 Tessalonicenses 5:17",
    verse_text: "Orai sem cessar.",
    reflection:
      "Orar sem cessar não é repetir fórmulas, é manter o coração conectado. É respirar com Deus. Como músico afinando o instrumento no meio do concerto, o cristão maduro ora no trânsito, no trabalho, na aflição e na alegria. A oração contínua não é carga, é companhia.",
    application:
      "Hoje, transforme intervalos curtos (elevador, fila, caminhada) em momentos de oração.",
    prayer:
      "Senhor, faze do meu dia inteiro uma oração. Que eu Te encontre no trabalho, no silêncio e na caminhada. Amém.",
    estimated_minutes: 4,
  },
  {
    id: "rot-friday",
    title: "Gratidão que Transforma",
    verse_ref: "1 Tessalonicenses 5:18",
    verse_text: "Em tudo dai graças, porque esta é a vontade de Deus em Cristo Jesus para convosco.",
    reflection:
      "Sexta-feira é um bom dia para olhar para trás e agradecer. Nem tudo foi fácil, mas Deus sustentou cada passo. A gratidão não é ignorar as dificuldades; é reconhecer que, mesmo nelas, houve graça.",
    application:
      "Escreva três bênçãos específicas desta semana. Compartilhe uma delas com alguém.",
    prayer:
      "Pai, obrigado pelas bênçãos que eu vi e pelas que nem percebi. Tua graça me sustenta. Amém.",
    estimated_minutes: 3,
  },
  {
    id: "rot-saturday",
    title: "Renovação Antes do Próximo Passo",
    verse_ref: "Isaías 40:31",
    verse_text:
      "Mas os que esperam no Senhor renovarão as suas forças, subirão com asas como águias; correrão, e não se cansarão; caminharão, e não se fatigarão.",
    reflection:
      "Sábado é tempo de respirar. O mundo grita por produtividade; o Reino sussurra: espera. Esperar no Senhor é ativo, é confiar que Ele renova aquilo que pareceu consumido durante a semana.",
    application:
      "Dedique um tempo hoje para algo que nutra a sua alma: leitura, natureza, comunhão com a família.",
    prayer:
      "Senhor, renova as minhas forças. Que eu não apenas corra, mas caminhe contigo. Amém.",
    estimated_minutes: 3,
  },
];

/** Day-of-year (1-366) used as rotation seed. */
const dayOfYear = (d = new Date()) => {
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d - start) / 86400000);
};

/** "YYYY-MM-DD" in the device's local timezone. */
export const todayKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Deterministic pick-one from a list, based on the calendar date. */
export const pickByDate = (items, d = new Date()) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const idx = dayOfYear(d) % items.length;
  return items[idx];
};

/**
 * Returns the devotional for today. Preference order:
 *  1. upstream (from /api/home or /api/devotionals/today), if it has real content
 *  2. local rotation (7 devotionals, weekday-indexed)
 */
export const getDailyDevotional = ({ upstream, now = new Date() } = {}) => {
  // Use upstream only if it looks meaningful (has title + reflection).
  if (upstream && typeof upstream === "object" && upstream.title && upstream.reflection) {
    // Same upstream every day? Still honour it, pastor's choice.
    return { ...upstream, source: "upstream" };
  }
  const weekday = now.getDay(); // 0..6
  const rotated = DEVOTIONAL_ROTATION[weekday % DEVOTIONAL_ROTATION.length];
  return { ...rotated, source: "rotation" };
};

/**
 * Returns a sermon recommendation for today. Deterministic per date.
 * Accepts the same list the app already fetches via /api/sermons or /api/home.
 */
export const getDailySermonRecommendation = (sermons, now = new Date()) => {
  if (!Array.isArray(sermons) || sermons.length === 0) return null;
  const picked = pickByDate(sermons, now);
  return picked || null;
};

/**
 * Bundle helper, everything the Home screen needs to render the day's journey.
 */
export const getDailyJourneyContent = ({ upstreamDevotional, sermons, now = new Date() } = {}) => {
  const devotional = getDailyDevotional({ upstream: upstreamDevotional, now });
  const sermon = getDailySermonRecommendation(sermons, now);
  return {
    date: todayKey(now),
    devotional,
    sermon,
  };
};
